import { produce } from 'immer';
import React, {
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import {
  basePrimitive,
  MarkupAction,
  MarkupItemType,
  type MarkupDataAddCallbackProps,
  type MarkupSummary,
  type TakeoffSystemState,
} from '@/lib/markup-designer';

interface TakeoffSystemContextType {
  takeoffSystem: TakeoffSystemState;
  setTakeoffSystem: Dispatch<SetStateAction<TakeoffSystemState>>;
}

// Contexts
const TakeoffSystemContext = React.createContext<
  TakeoffSystemContextType | undefined
>(undefined);
TakeoffSystemContext.displayName = 'TakeoffSystemContext';

// Initial State
const takeoffSystemState: TakeoffSystemState = {
  markupStack: undefined,
  action: MarkupAction.Default,
  isSaved: true,
  isSaving: false,
  isLoadingDrawing: true,
};
const initialState = takeoffSystemState;

// Provider
const TakeoffSystemProvider = (props: any) => {
  const [takeoffSystem, setTakeoffSystem] =
    useState<TakeoffSystemState>(initialState);
  const value = { takeoffSystem, setTakeoffSystem };

  return <TakeoffSystemContext.Provider {...props} value={value} />;
};

// Hook
function useTakeoffSystemClient() {
  const context = useContext(TakeoffSystemContext);

  if (!context) {
    throw new Error(
      `useTakeoffSystemClient must be used within the TakeoffSystemProvider`,
    );
  }

  const { takeoffSystem, setTakeoffSystem } = context;

  const setAction = (action: MarkupAction) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      return { ...prevTakeoffSystem, action };
    });
  };

  const setMarkup = (markup: MarkupSummary) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      const updatedTakeoffSystem = updateMarkupStack(markup, prevTakeoffSystem);

      return updatedTakeoffSystem;
    });
  };

  const addFeatures = (additions: any) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      // Use the previous state to calculate the updated state
      const updatedMarkup = applyAdditions(
        prevTakeoffSystem.markupStack!,
        additions,
      );

      const updatedTakeoffSystem = updateMarkupStack(
        updatedMarkup,
        prevTakeoffSystem,
      );

      return updatedTakeoffSystem;
    });
  };

  const deleteFeatures = (deletions: string[]) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      // handle deletions
      const deleteResult = applyDeletions(
        prevTakeoffSystem.markupStack!,
        deletions,
      );
      const updatedMarkup = deleteResult.newData;

      const updatedTakeoffSystem = updateMarkupStack(
        updatedMarkup,
        prevTakeoffSystem,
      );

      return updatedTakeoffSystem;
    });
  };

  const setIsSaved = (isSaved: boolean) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      return { ...prevTakeoffSystem, isSaved };
    });
  };

  const setIsSaving = (isSaving: boolean) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      return { ...prevTakeoffSystem, isSaving };
    });
  };

  const setIsLoadingDrawing = (isLoadingDrawing: boolean) => {
    setTakeoffSystem((prevTakeoffSystem) => {
      return { ...prevTakeoffSystem, isLoadingDrawing };
    });
  };

  const reset = () => {
    const updatedTakeoffSystem = initialState;

    setTakeoffSystem(updatedTakeoffSystem);
  };

  return {
    takeoffSystem,
    setAction,
    setMarkup,
    addFeatures,
    deleteFeatures,
    setIsSaved,
    setIsSaving,
    setIsLoadingDrawing,
    reset,
  };
}

// Combo export
export { TakeoffSystemProvider, useTakeoffSystemClient };

/**
 * Iterate through array of new features and add them to the relevant portion of markup data tree
 */
const applyAdditions = (
  original: MarkupSummary,
  additions: MarkupDataAddCallbackProps,
): MarkupSummary => {
  // prevent mutation
  const newData = produce(original, (draft) => {
    // Iterate through additions
    additions.features.forEach((f) => {
      switch (f.type) {
        case MarkupItemType.Point:
          draft.features[f.feature.id] = {
            ...basePrimitive(f.feature, f.type),
          };
          break;
        default:
          console.warn('Unknown change', f.type);
      }
    });
  });
  // return updated state
  return newData;
};

const applyDeletions = (original: MarkupSummary, deletions: string[]) => {
  const newData = produce(original, (draft) => {
    const deletedFeatureIds: string[] = original.deleted
      ? [...original.deleted.features]
      : [];

    deletions.forEach((featureId) => {
      deletedFeatureIds.push(featureId);

      delete draft.features[featureId];
    });

    draft.deleted = { features: deletedFeatureIds };
  });

  return { newData };
};

/**
 * Handle markup stack updates to allow for undo/redo
 */
const updateMarkupStack = (
  payload: MarkupSummary,
  state: TakeoffSystemState,
) => {
  return {
    ...state,
    markupStack: { ...payload },
    isSaved: false,
  };
};
